import React from "react";
import { X } from "lucide-react";

interface SavedAccount {
  username: string;
  name: string;
  firstName: string;
  lastName: string;
  imageUrl: string | null;
  organizationName: string;
  lastLogin: string;
}

interface AccountSwitcherProps {
  accounts?: SavedAccount[];
  currentUser?: any;
  onSelect: (account: SavedAccount) => void;
  onRemove?: (username: string) => void;
  variant?: "login" | "dropdown";
}

/**
 * AccountSwitcher Component
 * Displays saved accounts for switching between multiple users
 */
const AccountSwitcher: React.FC<AccountSwitcherProps> = ({
  accounts = [],
  currentUser = null,
  onSelect,
  onRemove,
  variant = "login",
}) => {
  if (accounts.length === 0) {
    return null;
  }

  const isLoginVariant = variant === "login";
  const isDropdownVariant = variant === "dropdown";

  return (
    <div className={`account-switcher ${isLoginVariant ? "w-full" : "w-full"}`}>
      <div className={`space-y-${isLoginVariant ? "2" : "1"}`}>
        {accounts.map(account => {
          const isCurrentUser = currentUser && currentUser.username === account.username;

          return (
            <div
              key={account.username}
              className={`
                account-item flex items-center gap-3 p-${isLoginVariant ? "3" : "2"}
                border border-gray-200 rounded-lg cursor-pointer
                transition-all duration-200
                ${isCurrentUser ? "bg-blue-50 border-blue-300" : "hover:bg-gray-50 hover:border-gray-300"}
                ${isLoginVariant ? "active:scale-[0.98]" : ""}
              `}
              onClick={() => onSelect(account)}
            >
              {/* Avatar */}
              <div
                className={`
                  flex-shrink-0 rounded-full overflow-hidden
                  ${isLoginVariant ? "w-12 h-12" : "w-10 h-10"}
                  bg-gradient-to-br from-blue-500 to-purple-600
                `}
              >
                {account.imageUrl ? (
                  <img src={account.imageUrl} alt={account.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white font-semibold text-sm">
                    {account.firstName?.[0]?.toUpperCase()}
                    {account.lastName?.[0]?.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Account Info */}
              <div className="flex-1 min-w-0">
                <div className={`font-medium text-gray-900 truncate ${isLoginVariant ? "text-base" : "text-sm"}`}>
                  {account.name}
                </div>
                <div className={`text-gray-500 truncate ${isLoginVariant ? "text-sm" : "text-xs"}`}>
                  {account.organizationName}
                </div>
                {isCurrentUser && <div className="text-xs text-blue-600 font-medium mt-0.5">Current Account</div>}
              </div>

              {/* Remove Button (only in dropdown variant and not for current user) */}
              {isDropdownVariant && !isCurrentUser && onRemove && (
                <button
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  onClick={e => {
                    e.stopPropagation();
                    onRemove(account.username);
                  }}
                  title="Remove account"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AccountSwitcher;
